import { apiClient } from '@indipia/shared';

export type SnsProvider = 'kakao' | 'naver' | 'google';

// ── 응답 타입 (모바일 auth_model.dart 동일 구조) ─────────────────────────

export interface SocialLoginSuccess {
  resultCode: 'SOCIAL_LOGIN_SUCCESS';
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  userId: number;
  loginId: string;
  userName: string;
  roles: string[];
  compId: number;
}

export interface SocialNeedRegister {
  resultCode: 'SOCIAL_NEED_REGISTER';
  provider: string;
  socialId: string;
  email: string | null;
  socialName: string | null;
  /** 카카오·네이버만 제공(콘솔 동의항목 등록 + 사용자 동의 시). 구글은 항상 null. */
  mobile: string | null;
  snsLinkToken: string;
}

export interface SocialRejected {
  resultCode: 'SOCIAL_REJECTED';
  message: string;
}

export type SocialLoginResult = SocialLoginSuccess | SocialNeedRegister | SocialRejected;

// ── API (모바일 auth_api.dart 동일 엔드포인트) ────────────────────────────

/** CSRF state 발급 — GET /gatepass/social/state */
export async function fetchOAuthState(): Promise<string> {
  const res = await apiClient.get('/gatepass/social/state');
  return (res.data.data as { state: string }).state;
}

/** 코드 교환 + 로그인 — POST /gatepass/social/login/{provider} */
export async function socialLogin(
  provider: SnsProvider,
  params: { code: string; redirectUri: string; state: string },
): Promise<SocialLoginResult> {
  const res = await apiClient.post(`/gatepass/social/login/${provider}`, params);
  return res.data.data as SocialLoginResult;
}

/** LINKED 케이스: snsLinkToken으로 JWT 발급 — POST /gatepass/social/login-with-token */
export interface SnsLoginTokens {
  accessToken:  string;
  refreshToken: string;
  userId:       number;
  loginId:      string;
  userName:     string;
  roles:        string[];
  compId:       number;
}

export async function loginWithSnsToken(snsLinkToken: string): Promise<SnsLoginTokens> {
  const res = await apiClient.post('/gatepass/social/login-with-token', { snsLinkToken });
  return res.data.data as SnsLoginTokens;
}

// ── OAuth URL 빌더 ────────────────────────────────────────────────────────
// 클라이언트 ID는 빌드 시 --dart-define 대응으로 VITE_ 환경변수로 주입

function getClientId(provider: SnsProvider): string {
  const map: Record<SnsProvider, string> = {
    kakao:  String(import.meta.env.VITE_KAKAO_CLIENT_ID  ?? ''),
    naver:  String(import.meta.env.VITE_NAVER_CLIENT_ID  ?? ''),
    google: String(import.meta.env.VITE_GOOGLE_CLIENT_ID ?? ''),
  };
  return map[provider];
}

export function buildOAuthUrl(provider: SnsProvider, state: string, redirectUri: string): string {
  const clientId = getClientId(provider);

  if (provider === 'kakao') {
    return `https://kauth.kakao.com/oauth/authorize?${new URLSearchParams({
      client_id: clientId, redirect_uri: redirectUri, response_type: 'code', state,
    })}`;
  }

  if (provider === 'naver') {
    return `https://nid.naver.com/oauth2.0/authorize?${new URLSearchParams({
      client_id: clientId, redirect_uri: redirectUri, response_type: 'code', state,
    })}`;
  }

  // google — scope, access_type, prompt 모바일과 동일
  return `https://accounts.google.com/o/oauth2/v2/auth?${new URLSearchParams({
    client_id: clientId, redirect_uri: redirectUri, response_type: 'code',
    scope: 'email profile', access_type: 'offline', prompt: 'consent', state,
  })}`;
}
